import React, { useState } from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";

const fmt = (val) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", minimumFractionDigits: 2 })?.format(val);

export default function MarketSimulator({ metal, result, onSimulate, resolvedBenchmark }) {
  const [eurUsd, setEurUsd] = useState(1.085);
  const [volatility, setVolatility] = useState(2.5);
  const [customBenchmark, setCustomBenchmark] = useState("");
  const [simResult, setSimResult] = useState(null);

  // Use the real resolved benchmark from the main calculation engine.
  // Only fall back to a generic placeholder if nothing is available yet.
  const baseBenchmark = resolvedBenchmark && resolvedBenchmark > 0 ? resolvedBenchmark : null;

  const handleSimulate = () => {
    const benchmark = customBenchmark ? parseFloat(customBenchmark) : baseBenchmark;
    if (!benchmark || benchmark <= 0) return;
    const eurAdjusted = benchmark / eurUsd * 1.085;
    const volAdjusted = eurAdjusted * (1 - volatility / 100);
    setSimResult({ benchmark, eurAdjusted, volAdjusted, eurUsd, volatility });
    if (onSimulate) onSimulate({ benchmark, eurAdjusted, volAdjusted });
  };

  return (
    <div className="card p-4 md:p-6 flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <Icon name="Globe" size={18} color="var(--color-accent)" strokeWidth={2} />
        <h3 className="font-heading font-semibold text-base" style={{ color: "var(--color-text-primary)" }}>
          Simulación de Mercado
        </h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* EUR/USD */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium" style={{ color: "var(--color-text-primary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
              Tipo EUR/USD
            </label>
            <span className="font-data text-sm font-semibold px-2 py-0.5 rounded" style={{ background: "var(--color-muted)", color: "var(--color-text-primary)" }}>
              {eurUsd?.toFixed(3)}
            </span>
          </div>
          <input
            type="range"
            min={0.9}
            max={1.3}
            step={0.001}
            value={eurUsd}
            onChange={(e) => setEurUsd(parseFloat(e?.target?.value))}
            className="w-full h-2 rounded-lg appearance-none cursor-pointer"
            style={{ accentColor: "var(--color-accent)" }}
          />
          <div className="flex justify-between text-xs mt-1" style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
            <span>0.900</span><span>1.100</span><span>1.300</span>
          </div>
          <p className="text-xs mt-1" style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
            Volatilidad histórica EUR/USD ya está en el buffer de riesgo. Este simulador añade un shock de punto en tiempo.
          </p>
        </div>

        {/* Volatility */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium" style={{ color: "var(--color-text-primary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
              Volatilidad LME
            </label>
            <span className="font-data text-sm font-semibold px-2 py-0.5 rounded" style={{ background: "var(--color-muted)", color: "var(--color-warning)" }}>
              {volatility}%
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={20}
            step={0.5}
            value={volatility}
            onChange={(e) => setVolatility(parseFloat(e?.target?.value))}
            className="w-full h-2 rounded-lg appearance-none cursor-pointer"
            style={{ accentColor: "var(--color-warning)" }}
          />
          <div className="flex justify-between text-xs mt-1" style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
            <span>0%</span><span>10%</span><span>20%</span>
          </div>
        </div>

        {/* Custom Benchmark */}
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: "var(--color-text-primary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
            Benchmark Manual (€/t)
          </label>
          <input
            type="number"
            placeholder={baseBenchmark ? `Base: ${baseBenchmark?.toFixed(2)}` : "Introduce un valor"}
            value={customBenchmark}
            onChange={(e) => setCustomBenchmark(e?.target?.value)}
            className="w-full px-3 py-2 rounded-lg border text-sm font-data"
            style={{
              borderColor: "var(--color-border)",
              background: "var(--color-card)",
              color: "var(--color-text-primary)",
              outline: "none",
            }}
          />
          <p className="text-xs mt-1" style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
            {baseBenchmark
              ? `Dejar vacío para usar el benchmark actual (${baseBenchmark?.toFixed(2)} €/t)`
              : "Dejar vacío para usar el benchmark actual"}
          </p>
        </div>
      </div>
      <Button
        variant="secondary"
        size="sm"
        iconName="Play"
        iconPosition="left"
        onClick={handleSimulate}
        disabled={!customBenchmark && !baseBenchmark}
      >
        Simular Escenario
      </Button>
      {!customBenchmark && !baseBenchmark && (
        <p className="text-xs" style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
          Calcula primero el precio máximo para usar el benchmark resuelto, o introduce un valor manual.
        </p>
      )}
      {simResult && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-1">
            <div className="rounded-lg p-3 text-center" style={{ background: "var(--color-muted)" }}>
              <p className="text-xs mb-1" style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>Benchmark Base</p>
              <p className="font-data font-semibold" style={{ color: "var(--color-text-primary)" }}>{fmt(simResult?.benchmark)}</p>
            </div>
            <div className="rounded-lg p-3 text-center" style={{ background: "var(--color-muted)" }}>
              <p className="text-xs mb-1" style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>Ajuste EUR/USD</p>
              <p className="font-data font-semibold" style={{ color: "var(--color-accent)" }}>{fmt(simResult?.eurAdjusted)}</p>
            </div>
            <div className="rounded-lg p-3 text-center" style={{ background: "var(--color-muted)" }}>
              <p className="text-xs mb-1" style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>Con Volatilidad</p>
              <p className="font-data font-semibold" style={{ color: "var(--color-warning)" }}>{fmt(simResult?.volAdjusted)}</p>
            </div>
          </div>

          {/* Interpretation block */}
          <div className="rounded-lg border p-4 flex flex-col gap-3" style={{ borderColor: "var(--color-border)", background: "var(--color-muted)" }}>
            <div className="flex items-center gap-2">
              <Icon name="BookOpen" size={15} color="var(--color-accent)" strokeWidth={2} />
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>Cómo interpretar estos resultados</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              <div className="flex flex-col gap-1">
                <span className="font-semibold" style={{ color: "var(--color-text-primary)" }}>Benchmark Base</span>
                <span style={{ color: "var(--color-text-secondary)" }}>Precio de referencia del metal en €/t antes de aplicar ningún ajuste. Si introduces un valor manual, se usa ese; si no, se toma el benchmark resuelto del sistema.</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="font-semibold" style={{ color: "var(--color-accent)" }}>Ajuste EUR/USD</span>
                <span style={{ color: "var(--color-text-secondary)" }}>Precio recalculado tras aplicar el tipo de cambio EUR/USD que has seleccionado. Un EUR más débil (EUR/USD bajo) encarece el metal cotizado en USD; uno más fuerte lo abarata.</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="font-semibold" style={{ color: "var(--color-warning)" }}>Con Volatilidad</span>
                <span style={{ color: "var(--color-text-secondary)" }}>Precio final tras descontar la volatilidad LME configurada. Representa el precio ajustado al riesgo: cuanto mayor sea la volatilidad, más conservador (bajo) será el precio resultante. Úsalo como precio de compra máximo en mercados inestables.</span>
              </div>
            </div>
            {simResult?.volatility > 10 && (
              <div className="flex items-start gap-2 mt-1 p-2 rounded-lg border border-amber-200 bg-amber-50">
                <Icon name="AlertTriangle" size={13} color="var(--color-warning)" strokeWidth={2} />
                <p className="text-xs" style={{ color: "var(--color-warning)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
                  Volatilidad alta ({simResult?.volatility}%): el precio con volatilidad es significativamente inferior al benchmark. Considera negociar un precio de compra más bajo o aplazar la operación.
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}